import React, { useState, useEffect, useRef } from 'react';
import '../styles/Root.css';
import '../styles/faireundon.css';

const FaireUnDon = () => {
    const optinInputRef = useRef(null);
    const [donationAmount, setDonationAmount] = useState(0);
    const [customerInfo, setCustomerInfo] = useState({
        lastName: '',
        firstName: '',
        birthDate: '',
        birthPlace: '',
        status: '',
        address: '',
        postalCode: '',
        city: '',
        phonePrefix: '+33',  // Valeur par défaut
        phone: '',
        email: '',
    });
    const [statusMessage, setStatusMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fadeElements = document.querySelectorAll('.fade-in');
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.opacity = 1;
                }
            });
        }, {
            threshold: 0.1
        });

        fadeElements.forEach(element => {
            observer.observe(element);
        });

        return () => {
            fadeElements.forEach(element => {
                observer.unobserve(element);
            });
        };
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCustomerInfo(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const validateForm = () => {
        setError(null);

        const requiredFields = ['lastName', 'firstName', 'birthDate', 'birthPlace', 'status', 'address', 'postalCode', 'city', 'phonePrefix', 'phone', 'email'];
        const missingFields = requiredFields.filter(field => !customerInfo[field]);

        if (missingFields.length > 0) {
            setError(`Veuillez remplir tous les champs obligatoires`);
            return false;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(customerInfo.email)) {
            setError('Veuillez entrer une adresse email valide');
            return false;
        }

        const phoneRegex = /^[0-9]{9,10}$/;
        if (!phoneRegex.test(customerInfo.phone)) {
            setError('Veuillez entrer un numéro de téléphone valide (9-10 chiffres)');
            return false;
        }

        if (donationAmount <= 0) {
            setError('Veuillez entrer un montant de don valide');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setStatusMessage('Préparation du paiement...');

        try {
            const paymentData = {
                email: customerInfo.email,
                amount: parseFloat(donationAmount),
                customer_id: `customer_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
                firstName: customerInfo.firstName,
                lastName: customerInfo.lastName,
                mobileNumber: `${customerInfo.phonePrefix}${customerInfo.phone}`,
                address: customerInfo.address,
                city: customerInfo.city,
                postcode: customerInfo.postalCode,
                donationAmount: parseFloat(donationAmount),
                birthDate: customerInfo.birthDate,
                birthPlace: customerInfo.birthPlace,
                status: customerInfo.status,
                cart: [] // No cart items for this payment
            };

            const response = await fetch('/api/faireundon.php?endpoint=create-payment-don', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(paymentData),
                mode: 'cors',
                credentials: 'include'
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Erreur de format de réponse' }));
                throw new Error(`Erreur serveur: ${response.status} - ${errorData.error || 'Erreur inconnue'}`);
            }

            const result = await response.json();

            if (result && result.payment_url && result.payment_id) {
                setStatusMessage('Redirection vers la page de paiement...');
                localStorage.setItem('current_payment_id', result.payment_id);
                localStorage.setItem('current_customer_id', paymentData.customer_id);
                window.location.href = result.payment_url;
            } else {
                setStatusMessage('');
                setError('Erreur: La réponse de paiement ne contient pas l\'URL nécessaire.');
            }
        } catch (error) {
            console.error("Erreur complète:", error);
            setStatusMessage('');
            setError(`Erreur lors de la préparation du paiement: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <main>
            <section className="hero fade-in">
                <h1>Soutenez l'Association des Amis du Musée des Troupes de Marine</h1>
                <p> Votre soutien est essentiel pour nous permettre de remplir nos missions et promouvoir l'histoire des Troupes de Marine.</p>
            </section>

            <section className="content-section fade-in">
                <h1>Je fais un don</h1>
                <p>L'Association des Amis du Musée des Troupes de Marine est reconnue d'intérêt général. L'habilitation à recevoir des dons ouvre droit à avantage fiscal.</p>
                <p><span style={{ color: 'var(--tdm-red)' }}>Particuliers, vous bénéficiez d'un crédit d'impôt de 66% du montant de votre don dans la limite de 20% de votre revenu imposable.</span></p>
            </section>

            <section className="form-section fade-in">
                <h2>Formulaire de Don</h2>
                {error && (
                    <div className="error-message" style={{
                        color: 'red',
                        padding: '10px',
                        marginBottom: '15px',
                        border: '1px solid red',
                        borderRadius: '4px',
                        backgroundColor: 'rgba(255, 0, 0, 0.1)'
                    }}>
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    <label htmlFor="last-name">Nom <span className="required">*</span></label>
                    <input
                        type="text"
                        id="last-name"
                        name="lastName"
                        value={customerInfo.lastName}
                        onChange={handleChange}
                        required
                    />

                    <label htmlFor="first-name">Prénom(s) <span className="required">*</span></label>
                    <input
                        type="text"
                        id="first-name"
                        name="firstName"
                        value={customerInfo.firstName}
                        onChange={handleChange}
                        required
                    />

                    <label htmlFor="birth-date">Date de naissance <span className="required">*</span></label>
                    <input
                        type="date"
                        id="birth-date"
                        name="birthDate"
                        value={customerInfo.birthDate}
                        onChange={handleChange}
                        required
                    />

                    <label htmlFor="birth-place">Lieu de naissance <span className="required">*</span></label>
                    <input
                        type="text"
                        id="birth-place"
                        name="birthPlace"
                        value={customerInfo.birthPlace}
                        onChange={handleChange}
                        required
                    />

                    <label htmlFor="status">Vous êtes <span className="required">*</span></label>
                    <select
                        id="status"
                        name="status"
                        value={customerInfo.status}
                        onChange={handleChange}
                        required
                    >
                        <option value="">Sélectionnez</option>
                        <option value="particulier">Particulier</option>
                        <option value="entreprise">Entreprise</option>
                    </select>

                    <label htmlFor="address">Adresse <span className="required">*</span></label>
                    <input
                        type="text"
                        id="address"
                        name="address"
                        value={customerInfo.address}
                        onChange={handleChange}
                        required
                    />

                    <label htmlFor="postal-code">Code Postal <span className="required">*</span></label>
                    <input
                        type="text"
                        id="postal-code"
                        name="postalCode"
                        value={customerInfo.postalCode}
                        onChange={handleChange}
                        required
                    />

                    <label htmlFor="city">Ville <span className="required">*</span></label>
                    <input
                        type="text"
                        id="city"
                        name="city"
                        value={customerInfo.city}
                        onChange={handleChange}
                        required
                    />

                    <label htmlFor="phone">Téléphone <span className="required">*</span></label>
                    <div className="phone-container">
                        <select
                            id="phone-prefix"
                            name="phonePrefix"
                            value={customerInfo.phonePrefix}
                            onChange={handleChange}
                            required
                        >
                            <option value="+33">France (+33)</option>
                        </select>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={customerInfo.phone}
                            onChange={handleChange}
                            placeholder="9 ou 10 chiffres"
                            required
                        />
                    </div>

                    <label htmlFor="email">E-mail <span className="required">*</span></label>
                    <input
                        type="email"
                        id="email"
                        name="email"
                        value={customerInfo.email}
                        onChange={handleChange}
                        required
                    />

                    <label htmlFor="donation-amount">Je souhaite donner (€) <span className="required">*</span></label>
                    <input
                        type="number"
                        id="donation-amount"
                        name="donationAmount"
                        value={donationAmount}
                        onChange={(e) => setDonationAmount(e.target.value)}
                        required
                    />

                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loading}
                    >
                        {loading ? 'Traitement...' : 'Je fais un don'}
                    </button>
                </form>
            </section>
        </main>
    );
};

export default FaireUnDon;
